import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import Form from '../../../../../components/Form'
import Languages from '../../../../ReusableFormFields/LanguagesFields/Languages'
import FormProgress from '../../../../../features/components/Form/FormProgress'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useSuccessGoogleLanguagesCreated from '../../../CampaignForms/CampaignGoogleCreate/CampaignGoogleLanguagesForm/hooks'

import { formatGoogleLocationsToBe } from '../../../CampaignForms/CampaignGoogleCreate/CampaignGoogleGeolocationsForm/fields'
import { transformLanguagesValuesToBE } from '../../../CampaignForms/CampaignGoogleCreate/CampaignGoogleLanguagesForm/fields'

import { createCampaignCriterions, clearCreateCampaignCriterions } from '../../../../../modules/actions/campaigns'
import { getChoicesCountries } from '../../../../../modules/actions/choices'
import { selectedGoogleAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  createCampaignCriterionsErrorSelector,
  createCampaignCriterionsIsLoadingSelector
} from '../../../../../modules/selectors/campaigns'
import { googleRightUploadAdDataSelector, rightSelector } from '../../../../../modules/selectors/rights'
import {
  choicesCountriesLoadingSelector,
  choicesCountriesSelector,
  choicesCountriesWasLoadedSelector
} from '../../../../../modules/selectors/choices'

import { initialValues, validationSchema } from './fields'
import { ANY_LANGUAGES, LANGUAGES_OPTION } from '../../../../ReusableFormFields/LanguagesFields/fields'
import { COUNTRIES } from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { GOOGLE_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { UPLOAD_CREATIVE_GOOGLE_LANGUAGES } from '../../../../../constants/forms'

import useDrawerFormStyles from '../../../../../styles/common/drawerForms'

const CampaignDetailsForm = ({ formProgressItems, onSuccessSubmit }) => {
  const drawerFormClasses = useDrawerFormStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { successSubmit, setSuccessSubmit } = useSuccessGoogleLanguagesCreated()

  const right = useSelector(rightSelector)
  const countriesWasLoaded = useSelector(choicesCountriesWasLoadedSelector)
  const countriesIsLoading = useSelector(choicesCountriesLoadingSelector)
  const countriesList = useSelector(choicesCountriesSelector)
  const { campaign } = useSelector(googleRightUploadAdDataSelector)
  const { id: googleAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const campaignId = campaign.id
  const populatedCountries = useMemo(
    () =>
      right[COUNTRIES].map(countryId => countriesList.find(({ id }) => id === countryId)).filter(country => !!country),
    [right, countriesList]
  )

  const onSubmit = useCallback(
    // countries are populated from the right, languages are taken from values
    values => {
      const hasPopulatedCountries = populatedCountries && !!populatedCountries.length

      if (values[LANGUAGES_OPTION] === ANY_LANGUAGES && !hasPopulatedCountries) {
        // If any languages and worldwide selected dont send request
        setSuccessSubmit(true)
      } else {
        // create criterions for languages
        const formattedLanguagesCriterions = transformLanguagesValuesToBE(values)
        const formattedLocationsCriterions = hasPopulatedCountries
          ? formatGoogleLocationsToBe(populatedCountries, 'resource_name')
          : []
        const formattedCriterions = [...formattedLanguagesCriterions, ...formattedLocationsCriterions]

        const formattedValues = {
          account: googleAdAccountId,
          operations: formattedCriterions.map(operation => ({
            campaign: campaignId,
            ...operation
          }))
        }

        dispatch(createCampaignCriterions(formattedValues))
      }
    },
    [dispatch, campaignId, googleAdAccountId, populatedCountries, setSuccessSubmit]
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const clearCreateCriterionsHandler = useCallback(() => {
    dispatch(clearCreateCampaignCriterions())
  }, [dispatch])

  useEffect(() => {
    if (!countriesWasLoaded) {
      dispatch(getChoicesCountries())
    }
  }, [dispatch, countriesWasLoaded])

  return (
    <div className={drawerFormClasses.formContainer}>
      <FormProgress items={formProgressItems} />
      <Form
        formik={purifiedFormik}
        formName={UPLOAD_CREATIVE_GOOGLE_LANGUAGES}
        successSubmit={successSubmit}
        onSuccessSubmit={onSuccessSubmit}
        clearHandler={clearCreateCriterionsHandler}
        errorSelector={createCampaignCriterionsErrorSelector}
        isLoadingSelector={createCampaignCriterionsIsLoadingSelector}
        isSubmitDataLoading={countriesIsLoading}
      >
        <h3 className={drawerFormClasses.formTitle}>{t('Which language(s) do you want to target?')}</h3>
        <Languages formik={formik} platform={GOOGLE_PLATFORM} />
      </Form>
    </div>
  )
}

export default CampaignDetailsForm
